import type {Country} from "../components/visualization/bubble-map/countries";
import type {SelectionResult} from "../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import type {BusinessUnit} from "./classes/MaterializedClasses";

export const GROUP_SUPER = 'customer_super' as const;
export const GROUP_USER = 'customer_user' as const;
export const GROUP_READ_ONLY = 'customer_readonly' as const;
export const GROUP_STAFF = 'mithra_staff' as const;
export const ALL_GROUPS = [GROUP_SUPER, GROUP_USER, GROUP_READ_ONLY, GROUP_STAFF];

export type HttpQueryParams = Record<string, boolean | number | number[] | string | string[]>

export declare interface GraphResponseNode {
    nodeId: number;
    name: string;
}

export declare interface GraphResponseLink<D> {
    source: number;
    target: number;
    value: D;
}

export declare interface GraphResponse<D = number> {
    nodes: GraphResponseNode[],
    links: GraphResponseLink<D>[],
}

export declare type PageResponse<D> = {
    count: number;
    next: string;
    previous: string;
    results: D[];
}

export declare type AdvancedDashboardPageResponse<D> = {
    columns: string[],
    rows: D[];
    count: number | undefined;
}

export declare interface TreeResponse<D = number> {
    label: string,
    children: TreeResponse<D>[],
    value: D,
}

export declare interface TreeValuesResponse<D> {
    id?: number,
    label: string,
    children: TreeValuesResponse<D>[],
    values: D,
}

export declare interface ShallowHierarchyApiResponse<D = number> {
    label: string;
    children: _ShallowHierarchyApiResponse<D>[];
    value: D;
}

declare interface _ShallowHierarchyApiResponse<D = number> {
    label: string;
    children: never[];
    value: D;
}

export declare interface HierarchyApiResponse<D = number> {
    label: string;
    children: HierarchyApiResponse<D>[];
    value: D;
}

export declare interface HierarchyMultiValueResponse<D = number> {
    label: string;
    children: HierarchyMultiValueResponse<D>[];
    values: D;
}

export type BaseHierarchy<D> = HierarchyApiResponse<D> | _ShallowHierarchyApiResponse<D>;

export type MatCommonKpiSerializer = {
    n_categories: number
    n_suppliers: number
    total_spend: number
    unclassified_spend: number
}
export type MatKpiTreeData = {
    spend: number
    parts: number
    distinct_suppliers: number
};
export declare type MatKpiTreeValues = keyof MatKpiTreeData;

export declare type JobPriceVarianceResp = {
    groups: {
        p__description: string;
        group_size: number;
        p__unit_cost__min: number;
        p__unit_cost__sum: number;
        p__quantity__sum: number;
        p__spend__sum: number;
        p__unit_cost__avg: number;
        pv_delta: number;
        pv: number;
        pv_opportunity: number;
    }[];
    total_opportunity: number;
};

/**
 * job_processing.views.JobConfigViewSet.list
 */
export interface ServerConfig {
    SKIP_AI_INTEGRATION: boolean;
    default_type: string;
    all_types: string[];
}

/**
 * KPI's, as taken from /api/bag/kpi_common/{:bagId}/{:CommonKpiBuilder_kpis}/
 * Generated by: http://localhost:8500/api/bag/kpi_common/1/all_names/?format=ts-type
 */
export declare type CommonKpiBuilder_kpis =
    'all'
    | 'get_all_labels'
    | 'get_business_units'
    | 'get_categories'
    | 'get_l1_categories'
    | 'get_suppliers'
    | 'get_total_categories'
    | 'get_total_l1_categories'
    | 'n_business_units'
    | 'n_categories'
    | 'n_l1_categories'
    | 'n_labels'
    | 'n_levels'
    | 'n_parts'
    | 'n_suppliers'
    | 'n_total_categories'
    | 'n_total_l1_categories'
    | 'n_unclassified'
    | 'total_pos_spend'
    | 'total_spend'
    | 'unclassified_pos_spend'
    | 'unclassified_spend';

/**
 * KPI's, as taken from /api/bag/kpi_viz/{:bagId}/{:CommonKpiBuilder_kpis}/
 * Generated by: http://localhost:8500/api/bag/kpi_viz/1/all_names/?format=ts-type
 */
export declare type UserVisualizationKpiBuilder_kpis =
    'categorization_spend_result'
    | 'count_tree'
    | 'count_tree_total'
    | 'get_all_categories'
    | 'get_l1'
    | 'out_spend_tree'
    | 'out_spend_tree_total'
    | 'spend_tree'
    | 'spend_tree_total'
    | 'total_used_labels';
export declare type KpiBuilder_kpis = UserVisualizationKpiBuilder_kpis;

export interface LoginUser {
    id: number
    username: string
    email: string
    first_name: string
    last_name: string
    groups: string[]
    is_staff: boolean
}

export interface MithraLoginResponse {
    access: string;
    refresh: string;
    user: LoginUser;
}

export type ppv_all_groups_Response = {
    ppv: {
        p__in_l1: "" | string,
        pv_opportunity__sum: number,
    }[]
}
export type job_data_Response = {
    p__id: string,
    p__description: string,
    s__id: string,
    bu__id: "" | string,
    p__spend: number,
    p__unit_cost: 0 | number,
    p__quantity: number,
    p__in_l1: "" | string,
    // And more
}
export type job_supplier_meta_Response = {
    s__id: string,
    p__spend__count: number,
    p__spend__sum: number,
    s__payment_term: number,
}

export enum SupplierSegmentationEnum {
    cr = 'Critical',
    st = 'Strategic',
    tr = 'Transactional',
}

export type DataFileUploadSerializer = {
    id: number;
    file_name: string;
    date: string;
}

export type DataFrameSerializer = {
    data: any[][]
    dataframe_size: number
    types: string[]
    columns_letters: string[]
    column_indexes: number[]
    columns: string[]
}

//TODO: it's not really a serializer, but it''s 
export type MithraColumnSerializer = {
    example: string;
    id: number;
    name: string;
    type: string;
    key: string;
    description: string;

    is_required: boolean;
    is_staff_only?: boolean;
    was_mapped: boolean;
}

export type DataMappingSerializer = {
    id?: number;
    data_file: number;
    client_column_name: string;
    client_column_type: string;
    client_column_index: number;
    mithra_column_key: string;
    ai_result: boolean;
    user_result: boolean;
}

export type TaxonomyMappingSerializer = {
    id?: number;
    taxonomy_file: number;
    client_column_name: string;
    client_column_type: string;
    client_column_index: number;
    mithra_column_key: string;
    ai_result: boolean;
    user_result: boolean;
}

export type DataIngestionKpiSerializer = {
    total_spend: number;
    suppliers: number;
    parts: number;
    business_units: number;
}

export type DataMappingResultTableSerializer = {
    data: string[][];
    columns: string[];
    column_indexes: number[];
    dataframe_size: number;
    types: string[];
}

// 'request_create_review' = ai run finished, 'live' = the job is closed and can't be changed
export enum AiCategorizationJobStatusEnum {
    ERROR = 'error',
    INIT = 'init',
    VERTEX_AI = 'vertex_ai',
    REQUEST_CREATE_REVIEW = 'request_create_review',
    CREATING_REVIEW = 'creating_review',
    REVIEW = 'review',
    REQUEST_CREATE_LIVE = 'request_create_live',
    CREATING_LIVE = 'creating_live',
    LIVE = 'live',
}

export type UserCategorizationJobRequestEnum =
    'categorize_all'

export type AiCategorizationJobCreateSerializer = {
    src_data_bag: number
    requested_task: UserCategorizationJobRequestEnum
}

export type TaxonomySuggestionSerializer<T> = {
    id: number;
    create_date: string;
    last_update: string;
    suggestion_state: T;
}

export type TaxonomyHealthCheckSerializer<T> = {
    id: number;
    create_date: string;
    last_update: string;
    suggestion_state: T;
    check_type: string; //FIXME: @Sina, the check_type must be saved into the suggestion_state.node.values
}

export type CategorizationVersionedStatisticsSerializer = {
    id: number,
    version: number,
    actor: number,
    created: string,
    total_parts: number
    average_confidence_score: number,
    very_low_confidence_parts: number,
    low_confidence_parts: number,
    medium_confidence_parts: number,
    high_confidence_parts: number,
    very_high_confidence_parts: number,
    reviewed_parts: number,
    reviewed_spend: number,
    unreviewed_parts: number,
    unreviewed_spend: number,
    categorized_reviewed_spend: number,
    categorized_unreviewed_spend: number,
    ai_categorization_job: number
    reviewed_total_pend: number
    unreviewed_total_spend: number
    covered_by_ai_spend: number
    covered_by_ai_total_spend: number
    covered_by_ai_reviewed_spend: number
    reviewed_total_spend: number
    covered_by_ai_unreviewed_spend: number
    covered_reviewed_suppliers: number
    covered_unreviewed_suppliers: number
    covered_suppliers: number
    total_suppliers: number
    covered_by_ai_reviewed_total_spend: number
    covered_by_ai_unreviewed_total_spend: number
    reviewed_transactions: number
    unreviewed_transactions: number
    total_transactions: number
    total_categories: number
    covered_categories: number
    uncovered_categories: number

    recategorized_spend: number
    recategorized_parts: number
    recategorized_total_spend: number
    recategorized_total_transactions: number
    recategorized_reviewed_spend: number
    recategorized_unreviewed_spend: number
    recategorized_reviewed_parts: number
    recategorized_unreviewed_parts: number
    categorized_spend: number
    categorized_parts: number
    categorized_total_spend: number
    categorized_total_transactions: number
    // categorized_reviewed_spend: number
    // categorized_unreviewed_spend: number
    categorized_reviewed_parts: number
    categorized_unreviewed_parts: number
    uncategorized_spend: number
    uncategorized_parts: number
    uncategorized_total_spend: number
    uncategorized_total_transactions: number
    uncategorized_reviewed_spend: number
    uncategorized_unreviewed_spend: number
    uncategorized_reviewed_parts: number
    uncategorized_unreviewed_parts: number
}

export type AdvancedTreeData = {
    value: number;
    label: string;
    children: AdvancedTreeData[];
};

type Coordinate = number;

/**
 * For countries
 */
export type AdvancedGeoData = {
    country: Country | '',
    spend: number,
    latitude: Coordinate,
    longitude: Coordinate,
    datafile?: string,
    color?: string
};

export type CategoryFilter = [string, string?, string?, string?];
export type Category234Filter = Array<string | undefined>;

export const BU_GROUP_FIELDS = [
    'bu_group_d0_id',
    'bu_group_d0_name',
    'bu_group_d1_id',
    'bu_group_d1_name',
    'bu_group_d2_id',
    'bu_group_d2_name',
] as const;
export type BuGroupField = typeof BU_GROUP_FIELDS[number];

export type AdvancedFilter = {
    country?: string[];
    s_ids?: SelectionResult<string>; // this is an array of strings of s_id's
    sp_ids?: SelectionResult<string>;
    business_unit_ids?: SelectionResult<number>;
    category_l1?: SelectionResult<string>;
    /** Several categories per level (Spend Landscape) */
    category_l2?: SelectionResult<string>;
    category_l3?: SelectionResult<string>;
    category_l4?: SelectionResult<string>;
    /** One category path below L1 (older filter, still accepted by the API) */
    category_l234?: Category234Filter;
    geo_regions?: SelectionResult<number | null>;
    date_range?: [string, string];
    c_descriptions?: string[];
    plants?: string[];
    c_address?: string[];
    src_databag_id?: number[];
    group_by_parent_supplier?: boolean;
    only_common_categories?: boolean;
    only_common_suppliers?: boolean;
} & Partial<Record<BuGroupField, SelectionResult<string>>>;

export type InsightsCategoryOption = {
    label: string
    total_spend: number
    /** The categories one level up that contain this label */
    parents: string[]
};

export type InsightsBuGroupOption = {
    value: string
    total_spend: number
};

export type AdvancedSpendPerGroup = {
    date: Date | string,
    spend: number,
    group: string,
    s_id?: string,
};

export type AdvancedSpendData = {
    spend: number
    group: string

    /**
     * Note: This field is overloaded and is used for both supplier id and parent supplier id (!)
     */
    s_id: string
}

export type DonutChartSupplierSpendData = {
    spend: number
    group: string
    s_id: string
} | {
    spend: number
    group: 'Other'
    s_id: null
};

export type AdvancedSpendKPI = {
    n_categories: number
    n_suppliers: number
    total_spend: number | null
    total_transactions: number | null
};

export type AdvancedFilterData = {
    id?: string,
    name: string,
    spend: number,
};

export type AdvancedContinentData = {
    id?: string,
    name: string,
    countries: string[],
    spend: number,
};

export type AdvancedOpportunityData = {
    spend: number
    active_l1: string
    active_l2: string
    active_l3: string
    active_l4: string
    opportunity_rank: number
}

export type BusinessUnitPivotData = (
    Exclude<AdvancedSupplierBreakdownData, 'business_units' | 'business_unit_ids'>
    & Record<`spend_bu_${number}`, number>
    & {spend: number}
    )

export type AdvancedBusinessUnitPivotData = AdvancedDashboardPageResponse<BusinessUnitPivotData> & {
    pivot_business_units: BusinessUnit[]
    bu_overflow: boolean | undefined
}

export type AdvancedSupplierBreakdownData = {
    s_id: string
    sp_id: string
    parent_s_name: string
    s_name: string
    sp_name: string
    s_country_code: string
    c_description: string
    spend: number
    active_l1: string
    active_l2: string
    active_l3: string
    active_l4: string
    databag_name: string
    business_units: string[]
    business_unit_ids: number[]
}

export type InsightsJobRunning = {
    gcp_running: number
}

export type ExcelDataColumns = {
    model?: string
    field: string
    title: string
}

export type TotalSpend = {
    total_spend: string
}

export type MaterialHarmonizationAdditionalInfo = {
    id: number,
    value: string,
    type_id: number,
    name: string,
    unit: string,
}

export enum GoldenRecordClusterType {
    Unknown = 0,
    Material = 1,
    Service = 2,
}

export type GoldenRecordCluster = {
    id: number,
    harmonized_name: string,
    note: string,
    type: GoldenRecordClusterType,
    transaction_count: number,
    supplier_count: number,
    spend_sum: number,
    limited_access_enabled: boolean,
    busy: boolean | undefined,
}

export type GoldenRecordPropertyType = {
    id: number,
    name: string,
    unit: string,
}

export type GoldenRecordClusterPropertyType = {
    id: number,
    type: GoldenRecordPropertyType,
    selected: boolean,
    modified: Date,
    transaction_count: number,
    supplier_count: number,
    spend_sum: number,
    cluster: number,
    busy: boolean | undefined,
}